import { Reveal, ChapterTag } from "@/components/Reveal";

export const Vision = () => (
  <section id="vision" data-testid="vision-section" className="scroll-mt-24 bg-[#F7F1E6] py-20 sm:py-28 lg:py-32">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-[1.15fr_1fr] lg:gap-16 lg:px-8">
      <Reveal y={40}>
        <div className="overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-40px_rgba(7,67,69,0.4)]">
          <img
            data-testid="vision-image"
            src="/assets/peanut-valet-product-packaging.webp"
            srcSet="/assets/peanut-valet-product-packaging-900.webp 900w, /assets/peanut-valet-product-packaging.webp 1672w"
            sizes="(max-width: 1024px) 100vw, 54vw"
            alt="Peanut Valet product rendering beside its matching teal retail packaging."
            width="1672"
            height="941"
            loading="lazy"
            className="h-auto w-full"
          />
        </div>
        <p className="mt-4 text-sm text-[#506460]" data-testid="vision-caption">
          The product and packaging vision, shown as design renderings.
        </p>
      </Reveal>

      <div>
        <Reveal>
          <ChapterTag number="04" label="Our Vision" />
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 text-[1.9rem] font-extrabold leading-tight tracking-tight text-[#074345] sm:text-4xl lg:text-[2.6rem]">
            Big personality. A shared vision.
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <p className="mt-5 text-base leading-relaxed text-[#243C39] sm:text-lg">
            Peanut Valet began with a simple frustration: enjoying in-shell peanuts
            often means dealing with scattered shells and skins. Our vision is a
            thoughtfully designed way to bring the shelling experience together in one
            place.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-4 text-base leading-relaxed text-[#243C39] sm:text-lg">
            We&rsquo;re developing that vision&mdash;from the machine itself to the
            packaging&mdash;and inviting early supporters and potential partners to
            follow along.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <div
            data-testid="founder-intro"
            className="mt-8 rounded-2xl border-l-4 border-[#F3802A] bg-[#EADCC6] p-6 sm:p-7"
          >
            <p className="text-lg font-extrabold text-[#074345]">David Hirschfield</p>
            <p className="mt-0.5 text-sm font-bold uppercase tracking-[0.18em] text-[#115B5B]">
              Founder &amp; Inventor
            </p>
            <p className="mt-3 text-base leading-relaxed text-[#243C39]">
              Peanut Valet began with a familiar frustration: enjoying in-shell peanuts
              while managing scattered shells and skins. David is developing a more
              contained, convenient snack-time experience and welcomes conversations
              with manufacturing, licensing, and product-development partners.
            </p>
          </div>
        </Reveal>
      </div>
      <Reveal y={36} className="mt-10 lg:col-span-2 lg:mt-16">
        <div
          data-testid="packaging-subsection"
          className="grid items-center gap-10 lg:grid-cols-[2fr_3fr] lg:gap-12"
        >
          <div>
            <p className="flex items-center gap-2.5 text-sm font-extrabold uppercase tracking-[0.22em] text-[#115B5B]">
              <span className="h-2 w-2 rounded-full bg-[#F3802A]" aria-hidden="true" />
              The Packaging Concept
            </p>
            <h3 className="mt-4 text-[1.7rem] font-extrabold leading-tight tracking-tight text-[#074345] sm:text-3xl">
              A fresh twist, right down to the box.
            </h3>
            <p className="mt-4 text-base leading-relaxed text-[#243C39] sm:text-lg">
              Bold teal branding. Clear &ldquo;Pour. Turn. Enjoy.&rdquo; instructions. A
              personality made for snack time. This packaging concept brings the Peanut
              Valet story together, showing how the product, its features, and everyday
              moments could come to life for future customers and potential retail,
              manufacturing, and licensing partners.
            </p>
          </div>
          <div>
            <a
              href="/assets/peanut-valet-packaging-concept-full.jpg"
              target="_blank"
              rel="noopener"
              data-testid="packaging-image-link"
              aria-label="Open the full-size Peanut Valet packaging concept artwork in a new tab"
              className="mx-auto block max-w-[560px] overflow-hidden rounded-[1.5rem] shadow-[0_30px_60px_-35px_rgba(7,67,69,0.45)] transition-transform duration-300 hover:scale-[1.01]"
            >
              <img
                data-testid="packaging-image"
                src="/assets/peanut-valet-packaging-concept.webp"
                srcSet="/assets/peanut-valet-packaging-concept-768.webp 768w, /assets/peanut-valet-packaging-concept.webp 1024w"
                sizes="(max-width: 1024px) 100vw, 560px"
                alt="Peanut Valet packaging concept showing branded artwork, Pour Turn Enjoy instructions, product features, and snack-time scenes."
                width="1024"
                height="1536"
                loading="lazy"
                className="h-auto w-full"
              />
            </a>
            <div className="mx-auto max-w-[560px]">
              <a
                href="/assets/peanut-valet-packaging-concept-full.jpg"
                target="_blank"
                rel="noopener"
                data-testid="packaging-fullsize-link"
                className="mt-3 inline-block text-sm font-bold text-[#074345] underline decoration-[#F3802A] decoration-2 underline-offset-4 transition-colors hover:text-[#115B5B]"
              >
                View full-size packaging concept.
              </a>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
